import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useError } from "../context/ErrorContext";
import { apiService } from "../services/apiService";
import { CheckIcon } from "@heroicons/react/24/outline";

const SubscriptionSettings = () => {
  const navigate = useNavigate();
  const { showError } = useError();
  const [currentPlan, setCurrentPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionChanged, setSubscriptionChanged] = useState(false);

  const plans = {
    free: {
      name: "Free",
      price: "$0",
      period: "/forever",
      features: [
        "50 AI chat requests/month",
        "15 summaries/month",
        "10 detailed AI notes/month",
        "5 test generations/month",
        "Basic note organization",
        "Community support",
      ],
    },
    student: {
      name: "Student",
      price: "$5",
      period: "/per month",
      features: [
        "1,200 AI chat requests",
        "200 document summaries",
        "120 detailed AI notes",
        "90 test generations",
        "Advanced note organization",
        "Priority support",
      ],
      popular: true,
    },
    professional: {
      name: "Professional",
      price: "$25",
      period: "/per month",
      features: [
        "Unlimited AI chat requests",
        "Unlimited document summaries",
        "360 detailed AI notes",
        "270 test generations",
        "Advanced note organization",
        "Priority support",
      ],
    },
  };

  useEffect(() => {
    const loadSubscription = async () => {
      try {
        const profile = await apiService.get("/api/profile");
        setCurrentPlan(profile.subscription);
      } catch (error) {
        showError("Failed to load subscription details");
      } finally {
        setLoading(false);
        setSubscriptionChanged(false);
      }
    };

    loadSubscription();
  }, [showError, subscriptionChanged]);

  const handleUpgrade = async (planName) => {
    try {
      const profile = await apiService.post("/api/subscription/switch-plan", {
        plan: planName,
      });

      setCurrentPlan(profile.subscription);
      showError(`Successfully switched to ${planName} plan`, "success");
      navigate("/profile");
    } catch (error) {
      showError("Failed to switch plan");
    }
  };

  const handleCancel = async () => {
    if (window.confirm("Are you sure you want to cancel your subscription?")) {
      try {
        const profile = await apiService.post("/api/subscription/cancel");
        setCurrentPlan(profile.subscription);
        showError("Subscription cancelled successfully", "success");
      } catch (error) {
        showError("Failed to cancel subscription");
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-bg text-white p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Subscription Settings</h1>
          <button
            onClick={() => navigate("/settings/billing")}
            className="px-4 py-2 bg-dark-card text-white rounded-lg hover:bg-opacity-80 transition-colors"
          >
            Manage Billing
          </button>
        </div>

        {/* Current Plan Info */}
        <div className="bg-dark-card rounded-xl p-6 mb-8">
          <h2 className="text-xl font-semibold mb-4">Current Plan</h2>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xl font-bold text-blue-500">
                {plans[currentPlan?.plan || "free"].name}
              </p>
              <p className="text-gray-400">
                {currentPlan?.status === "active"
                  ? `Active ${plans[currentPlan.plan].name} subscription`
                  : "No active subscription"}
              </p>
            </div>
            {currentPlan?.plan !== "free" &&
              currentPlan?.status === "active" && (
                <button
                  onClick={handleCancel}
                  className="px-4 py-2 text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                >
                  Cancel Subscription
                </button>
              )}
          </div>
        </div>

        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {Object.entries(plans).map(([planId, plan]) => (
            <div
              key={planId}
              className={`relative bg-dark-card rounded-xl p-6 ${
                plan.popular ? "ring-2 ring-blue-500" : ""
              }`}
            >
              {plan.popular && (
                <span className="absolute -top-3 right-4 bg-blue-500 text-white px-3 py-1 rounded-full text-sm">
                  Most Popular
                </span>
              )}

              <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
              <div className="flex items-baseline mb-6">
                <span className="text-4xl font-bold">{plan.price}</span>
                <span className="text-gray-400 ml-1">{plan.period}</span>
              </div>

              <ul className="space-y-4 mb-8">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-center">
                    <CheckIcon className="w-5 h-5 text-green-500 mr-2" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              {currentPlan?.plan !== planId ? (
                <button
                  onClick={() => handleUpgrade(planId)}
                  className={`w-full py-3 rounded-lg font-medium transition-colors ${
                    planId === "free"
                      ? "bg-gray-700 text-white hover:bg-gray-600"
                      : "bg-blue-500 text-white hover:bg-blue-600"
                  }`}
                >
                  {currentPlan?.plan === "free" ? "Upgrade" : "Switch Plan"}
                </button>
              ) : (
                <button
                  disabled
                  className="w-full py-3 rounded-lg font-medium bg-green-500/20 text-green-500 cursor-not-allowed"
                >
                  Current Plan
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionSettings;
